import { Input } from "antd"
import React, { type ComponentProps, useRef, useState } from "react"

import { useDebouncedCallback } from "../../hooks/use-debounced-callback.ts"
import { SEARCH_DEBOUNCE_MS } from "./search-debounce.ts"

interface DebouncedInputProps
  extends Omit<ComponentProps<typeof Input>, "value" | "onChange"> {
  value?: string | null
  delay?: number
  /** Bump on reset to drop the draft and the commit still in flight. */
  resetSignal?: number
  onValueChange: (value: string | null) => void
}

/**
 * Controlled, so a Clear All that nulls the filter also empties the field.
 *
 * The draft re-syncs on an external change and only on one — echoing back the
 * caller's own debounced update would move the caret mid-word. A reset cannot
 * be read off the value, since clearing a filter that already reads null is no
 * change at all, so callers signal it through `resetSignal` instead.
 */
export const DebouncedInput = React.memo(
  ({
    value,
    delay = SEARCH_DEBOUNCE_MS,
    resetSignal,
    onValueChange,
    ...props
  }: DebouncedInputProps) => {
    const [draft, setDraft] = useState(value ?? "")
    const [committed, setCommitted] = useState(value ?? "")
    const pending = useRef(false)
    const lastReset = useRef(resetSignal)

    const wasReset = resetSignal !== lastReset.current
    if (wasReset || (value ?? "") !== committed) {
      lastReset.current = resetSignal
      setCommitted(value ?? "")
      setDraft(value ?? "")
      pending.current = false
    }

    const commit = useDebouncedCallback((next: string) => {
      if (!pending.current) return
      pending.current = false
      setCommitted(next)
      onValueChange(next || null)
    }, delay)

    return (
      <Input
        {...props}
        value={draft}
        allowClear
        onChange={(e) => {
          pending.current = true
          setDraft(e.target.value)
          commit(e.target.value)
        }}
      />
    )
  }
)

DebouncedInput.displayName = "DebouncedInput"
